import React, { useMemo } from 'react';
import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis,
  Tooltip, ResponsiveContainer, ReferenceLine, Legend, Cell
} from 'recharts';

/**
 * Scatter plot for Options Explorer.
 * X = DTE (days to expiration)
 * Y = simulated % return at the current scenario slider value
 * Color = moneyness bucket (gradient from OTM warm to ITM cool)
 * Shape = ticker (via separate Scatter layers)
 * Size = premium
 * Click = pin/unpin contract (max 8)
 */

// Moneyness color palette (OTM = warm/amber, ATM = gray, ITM = cool/blue)
const MONEYNESS_COLORS = {
  '100% OTM': '#f59e0b',
  '50% OTM': '#f97316',
  '35% OTM': '#fb923c',
  '20% OTM': '#fbbf24',
  '10% OTM': '#fcd34d',
  '5% OTM': '#fde68a',
  'ATM': '#9ca3af',
  '5% ITM': '#93c5fd',
  '10% ITM': '#60a5fa',
  '20% ITM': '#3b82f6',
  '35% ITM': '#2563eb',
  '50% ITM': '#1d4ed8',
};

const DEFAULT_COLOR = '#6b7280';

// Ticker shapes — Recharts supports: circle, cross, diamond, square, star, triangle, wye
const TICKER_SHAPES = ['circle', 'square', 'diamond', 'triangle', 'star', 'cross', 'wye'];

function getContractColor(moneynessBucket) {
  return MONEYNESS_COLORS[moneynessBucket] || DEFAULT_COLOR;
}

function getScenarioReturn(contract, scenarioValue) {
  // Find the closest matching scenario key
  const key = String(scenarioValue);
  const scenario = contract.scenarios?.[key];
  if (scenario) return scenario.pct_return;

  // Fallback: find nearest key
  const keys = Object.keys(contract.scenarios || {}).map(Number).sort((a, b) => a - b);
  let closest = keys[0];
  let minDiff = Infinity;
  for (const k of keys) {
    if (Math.abs(k - scenarioValue) < minDiff) {
      minDiff = Math.abs(k - scenarioValue);
      closest = k;
    }
  }
  return contract.scenarios?.[String(closest)]?.pct_return ?? 0;
}

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0]?.payload;
  if (!data) return null;

  return (
    <div className="bg-gray-900 border border-gray-600 rounded-lg p-3 shadow-lg text-xs max-w-xs">
      <div className="font-bold text-white mb-1">
        {data.ticker} ${data.strike} {data.option_type}
      </div>
      <div className="space-y-0.5 text-gray-300">
        <div>Expiration: {data.expiration} ({data.dte}d)</div>
        <div>Moneyness: {data.moneyness_label}</div>
        <div>Premium: ${data.premium?.toFixed(2)}</div>
        <div>IV: {(data.iv * 100).toFixed(1)}%</div>
        <div className={`font-medium ${data._yValue >= 0 ? 'text-green-400' : 'text-red-400'}`}>
          Return at scenario: {data._yValue >= 0 ? '+' : ''}{data._yValue?.toFixed(1)}%
        </div>
      </div>
      <div className="text-gray-500 mt-1">Click to {data._isPinned ? 'unpin' : 'pin'}</div>
    </div>
  );
};

function ExplorerScatterPlot({ contracts, currentScenario, pinnedContracts, onTogglePin, isPinned }) {
  // Group contracts by ticker for different shapes
  const { tickerGroups, tickerShapeMap, maxPremium } = useMemo(() => {
    const tickers = [...new Set(contracts.map(c => c.ticker))].sort();
    const shapeMap = {};
    tickers.forEach((t, i) => {
      shapeMap[t] = TICKER_SHAPES[i % TICKER_SHAPES.length];
    });

    const groups = {};
    let maxP = 0;

    for (const c of contracts) {
      const yValue = getScenarioReturn(c, currentScenario);
      const premium = c.premium || 0;
      if (premium > maxP) maxP = premium;

      const point = {
        ...c,
        x: c.dte,
        y: yValue,
        _yValue: yValue,
        _isPinned: isPinned(c),
        _color: getContractColor(c.moneyness_bucket),
        _premium: premium,
      };

      if (!groups[c.ticker]) groups[c.ticker] = [];
      groups[c.ticker].push(point);
    }

    return { tickerGroups: groups, tickerShapeMap: shapeMap, maxPremium: maxP };
  }, [contracts, currentScenario, isPinned]);

  if (contracts.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        No contracts to display. Adjust filters or generate contracts.
      </div>
    );
  }

  // Moneyness legend items (only show those present in data)
  const presentBuckets = [...new Set(contracts.map(c => c.moneyness_bucket))];
  const orderedBuckets = Object.keys(MONEYNESS_COLORS).filter(b => presentBuckets.includes(b));

  return (
    <div className="bg-gray-800 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-200">
          Contract Discovery — {contracts.length} contracts
        </h3>
        <span className="text-xs text-gray-400">
          {pinnedContracts.length}/8 pinned
        </span>
      </div>

      {/* Color legend (moneyness) */}
      <div className="flex flex-wrap gap-2 mb-2">
        {orderedBuckets.map(bucket => (
          <span key={bucket} className="flex items-center gap-1 text-xs text-gray-400">
            <span
              className="w-3 h-3 rounded-full inline-block"
              style={{ backgroundColor: MONEYNESS_COLORS[bucket] }}
            />
            {bucket}
          </span>
        ))}
      </div>

      {/* Shape legend (ticker) */}
      {Object.keys(tickerGroups).length > 1 && (
        <div className="flex flex-wrap gap-3 mb-3 text-xs text-gray-400">
          <span>Shapes:</span>
          {Object.entries(tickerShapeMap).map(([ticker, shape]) => (
            <span key={ticker}>
              {shape} = {ticker}
            </span>
          ))}
        </div>
      )}

      {/* Chart */}
      <ResponsiveContainer width="100%" height={420}>
        <ScatterChart margin={{ top: 10, right: 20, bottom: 40, left: 20 }}>
          <XAxis
            dataKey="x"
            type="number"
            name="DTE"
            label={{ value: 'Days to Expiration', position: 'bottom', offset: 20, fill: '#9ca3af', fontSize: 12 }}
            tick={{ fill: '#9ca3af', fontSize: 11 }}
            stroke="#374151"
          />
          <YAxis
            dataKey="y"
            type="number"
            name="Return"
            label={{ value: `Return at ${currentScenario > 0 ? '+' : ''}${currentScenario}%`, angle: -90, position: 'insideLeft', offset: 10, fill: '#9ca3af', fontSize: 12 }}
            tick={{ fill: '#9ca3af', fontSize: 11 }}
            stroke="#374151"
            tickFormatter={(v) => `${v.toFixed(0)}%`}
          />
          <ZAxis
            dataKey="_premium"
            range={[40, 400]}
            name="Premium"
          />
          <ReferenceLine y={0} stroke="#6b7280" strokeDasharray="4 4" />
          <Tooltip content={<CustomTooltip />} />

          {/* One Scatter layer per ticker (for different shapes) */}
          {Object.entries(tickerGroups).map(([ticker, points]) => (
            <Scatter
              key={ticker}
              name={ticker}
              data={points}
              shape={tickerShapeMap[ticker] || 'circle'}
              onClick={(data) => onTogglePin(data)}
              cursor="pointer"
            >
              {points.map((point, idx) => (
                <Cell
                  key={idx}
                  fill={point._color}
                  stroke={point._isPinned ? '#ffffff' : 'none'}
                  strokeWidth={point._isPinned ? 2.5 : 0}
                  fillOpacity={point._isPinned ? 1 : 0.75}
                />
              ))}
            </Scatter>
          ))}
        </ScatterChart>
      </ResponsiveContainer>

      <p className="text-xs text-gray-500 text-center mt-2">
        Click any dot to pin it for heatmap analysis. Color = moneyness, size = premium cost.
      </p>
    </div>
  );
}

export default ExplorerScatterPlot;
